import { Link, useParams } from 'react-router-dom';

import { Skeleton } from '../../components/Skeleton';
import { useAccountingView } from '../../hooks/useAccountingView';

import styles from './Accounting.module.css';
import { MoneyCell, commitsSub } from './MoneyCell';
import {
  INVESTMENT_LABELS,
  fmtPct,
  rollupInvestmentByFunctionForEngineer,
} from './rollups';
import type { InvestmentType } from './types';

const ROWS: Array<{ key: InvestmentType; className: string }> = [
  { key: 'ktlo', className: styles.rowKtlo! },
  { key: 'enablement', className: styles.rowEnablement! },
  { key: 'growth', className: styles.rowGrowth! },
];

/**
 * Per-engineer drill-down. Investment × BusinessFunction matrix
 * scoped to a single engineer's commits. Reached via the engineer
 * name link in the Teams view.
 */
export function EngineerDetailView() {
  const params = useParams<{ engineerId?: string }>();
  const { data, isPending, error } = useAccountingView();

  if (isPending) return <Skeleton title='Engineer detail' rows={5} />;
  if (error) return <div className={styles.errorBox}>Failed to load accounting view: {error.message}</div>;
  if (!data) return null;

  const engineerId = params.engineerId ? decodeURIComponent(params.engineerId) : null;
  const engineer = engineerId
    ? data.engineers.find((e) => e.id === engineerId) ?? null
    : null;

  if (!engineer) {
    return (
      <>
        <Link to='/accounting' className={styles.backLink}>
          ← Back to accounting
        </Link>
        <div className={styles.errorBox}>
          No engineer with id <code>{engineerId ?? '—'}</code>.
        </div>
      </>
    );
  }

  const r = rollupInvestmentByFunctionForEngineer(data, engineer.id);
  const bfs = data.business_functions;

  return (
    <>
      <Link to='/accounting' className={styles.backLink}>
        ← Back to accounting
      </Link>
      <h2 className='section-h2' style={{ marginTop: 0 }}>
        {engineer.display_name}
        {engineer.kind === 'agent' ? ' 🤖' : ''}
      </h2>
      {engineer.operator_engineer_id ? (
        <p className='muted' style={{ marginTop: 0, marginBottom: 'var(--4u)' }}>
          Operated by{' '}
          <Link
            to={`/accounting/engineer/${encodeURIComponent(engineer.operator_engineer_id)}`}
          >
            {data.engineers.find((e) => e.id === engineer.operator_engineer_id)?.display_name ?? 'unknown'}
          </Link>
          . Agent activity rolls up to its operator&apos;s cost center; token cost is
          attributed separately (out of scope for POC).
        </p>
      ) : null}

      <table className={styles.matrix}>
        <thead>
          <tr>
            <th>Investment Type</th>
            {bfs.map((bf) => (
              <th key={bf.id}>{bf.display}</th>
            ))}
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map(({ key, className }) => {
            const rowTotal = r.investmentTotals[key];
            return (
              <tr key={key}>
                <td className={className}>{INVESTMENT_LABELS[key]}</td>
                {bfs.map((bf) => {
                  const cell = r.cells[`${key}|${bf.id}`];
                  return (
                    <td key={bf.id}>
                      <MoneyCell cost={cell?.cost_usd ?? 0} sub={commitsSub(cell?.count ?? 0)} />
                    </td>
                  );
                })}
                <td>
                  <MoneyCell
                    cost={rowTotal.cost_usd}
                    sub={`${fmtPct(rowTotal.cost_usd, r.grandTotal.cost_usd)} of total`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td>Total</td>
            {bfs.map((bf) => {
              const colTotal = r.functionTotals[bf.id];
              return (
                <td key={bf.id}>
                  <MoneyCell
                    cost={colTotal?.cost_usd ?? 0}
                    sub={commitsSub(colTotal?.count ?? 0)}
                  />
                </td>
              );
            })}
            <td>
              <MoneyCell cost={r.grandTotal.cost_usd} sub={commitsSub(r.grandTotal.count)} />
            </td>
          </tr>
        </tfoot>
      </table>
    </>
  );
}
